/**
 * LA POLICE DE SECOURS DU PDF.
 *
 * Le document est écrit en « helvetica », une des 14 polices standard de jsPDF,
 * qui ne connaît que WinAnsi. Tant que le texte imprimé est la version LATINE
 * de la fiche, cela suffit. Il reste un cas : celui où aucune romanisation
 * n'est sortie, et où c'est l'ORIGINAL qu'il faut imprimer plutôt qu'un vide.
 *
 * DejaVu Sans est embarquée pour ce cas-là. Elle porte le latin, le cyrillique,
 * le grec, l'arabe, l'hébreu, le thaï, l'arménien et le géorgien. Elle ne porte
 * AUCUN idéogramme : ni han, ni kana, ni hangul. C'est un choix assumé — une
 * police CJK pèse une dizaine de mégaoctets, et « any-ascii » romanise
 * correctement le chinois et le coréen, si bien que le repli n'y sert jamais.
 *
 * Le fichier n'est PAS empaqueté dans le JavaScript : 757 Ko de police
 * deviendraient un mégaoctet de base64 chargé par tous les utilisateurs, pour
 * un cas qui ne se présente presque jamais. Il est servi depuis « public/ » et
 * lu à la demande — par « fetch » dans le navigateur, par le disque côté
 * serveur, où « public/ » est bien présent dans l'image (voir le Dockerfile).
 *
 * « generateInvoicePDF » est SYNCHRONE, et l'était avant ce chantier : une
 * quinzaine d'écrans l'appellent ainsi. Le chargement, lui, ne peut pas l'être.
 * D'où la séparation : « chargerPoliceUnicode() » s'attend AVANT la génération,
 * et « policeUnicodeDisponible() » dit ensuite, sans attente, si le PDF peut y
 * recourir. Un PDF engendré sans ce préchargement reste correct : il imprime la
 * romanisation, et signale les rares champs qu'il n'a pas su écrire.
 */

import type { jsPDF } from "jspdf";

export const POLICE_UNICODE = "DejaVuSans";
const CHEMIN_PUBLIC = "/fonts/DejaVuSans.ttf";
/** Le même fichier, vu depuis la racine du processus serveur. */
export const CHEMIN_DISQUE = ["public", "fonts", "DejaVuSans.ttf"] as const;

/** Le base64 de la police, une fois lu. « null » = pas encore chargée. */
let base64: string | null = null;
/** Le chargement en cours, pour que dix appels simultanés n'en déclenchent qu'un. */
let chargement: Promise<string | null> | null = null;

/**
 * CE MODULE NE DOIT CONTENIR AUCUN APPEL À NODE, ET C'EST UNE LEÇON PAYÉE.
 *
 * Il est chargé par « data-provider.tsx », qui est un composant CLIENT. Une
 * première version y lisait la police au disque, derrière un
 * « typeof window === "undefined" » — garde parfaitement inutile ici : webpack
 * ne l'évalue pas, il suit les « import » et tente d'empaqueter « node:path »
 * et « node:fs/promises » pour le navigateur. La construction échoue avec
 * « UnhandledSchemeError ».
 *
 * La lecture au disque vit donc dans « pdf-polices-serveur.ts », que seul du
 * code serveur importe. Les deux se rejoignent par « deposerPoliceUnicode ».
 *
 * (Le défaut n'était visible qu'à froid : trois constructions locales
 * successives l'ont laissé passer sur un « .next » en cache, et c'est la CI,
 * partant d'un répertoire vide, qui l'a révélé.)
 */
async function lireLaPolice(): Promise<string | null> {
    if (typeof window === "undefined") {
        // Côté serveur, c'est « chargerPoliceUnicodeServeur » qui dépose la
        // police. Rien à faire ici, et surtout rien à importer.
        return null;
    }

    const reponse = await fetch(CHEMIN_PUBLIC);
    if (!reponse.ok) throw new Error(`police introuvable (${reponse.status})`);
    const octets = new Uint8Array(await reponse.arrayBuffer());

    // « btoa » sur une chaîne construite en une fois dépasse la pile des
    // arguments au-delà de ~100 Ko ; on la bâtit par tranches.
    let binaire = "";
    for (let i = 0; i < octets.length; i += 0x8000) {
        binaire += String.fromCharCode(...octets.subarray(i, i + 0x8000));
    }
    return btoa(binaire);
}

/** Dépose une police déjà lue. Seul « pdf-polices-serveur.ts » l'appelle. */
export function deposerPoliceUnicode(donnees: string): void {
    base64 = donnees;
}

/**
 * Charge la police, une fois pour toutes. Idempotent, et sans jamais lever :
 * l'absence de police dégrade le PDF, elle ne doit pas empêcher de l'émettre.
 */
export async function chargerPoliceUnicode(): Promise<boolean> {
    if (base64) return true;
    if (!chargement) {
        chargement = lireLaPolice().catch((erreur) => {
            // « console.warn » et non « log » : « next.config.ts » applique
            // « removeConsole » en production, dont seuls « error » et « warn »
            // sont exemptés. Un « log » ici serait muet là où il compte.
            console.warn("[PDF] police Unicode indisponible :", erreur?.message);
            return null;
        });
    }
    base64 = await chargement;
    return base64 !== null;
}

/** Sans attente : la police est-elle prête à être posée sur un document ? */
export function policeUnicodeDisponible(): boolean {
    return base64 !== null;
}

/**
 * Déclare la police sur UN document. À rappeler pour chaque document : le
 * registre de polices de jsPDF est propre à l'instance.
 */
export function installerPoliceUnicode(doc: jsPDF): boolean {
    if (!base64) return false;
    const registre = doc as unknown as {
        addFileToVFS: (n: string, d: string) => void;
        addFont: (f: string, n: string, s: string) => void;
        getFontList: () => Record<string, unknown>;
    };
    if (registre.getFontList()[POLICE_UNICODE]) return true;

    registre.addFileToVFS("DejaVuSans.ttf", base64);
    registre.addFont("DejaVuSans.ttf", POLICE_UNICODE, "normal");
    return true;
}

/**
 * LES OPTIONS BIDIRECTIONNELLES, établies par l'expérience et non par la
 * documentation.
 *
 * jsPDF fait DÉJÀ deux choses tout seul, sur chaque appel à « text() » :
 *
 *  - « processArabic », branché sur l'événement « preProcessText », remplace
 *    les lettres arabes par leurs formes contextuelles liées. Vérifié :
 *    « شارع » (0634 0627 0631 0639) ressort en 0xFEB7 0xFE8E 0xFEAD 0xFEC9,
 *    des formes de présentation. Rien à faire pour l'obtenir.
 *
 *  - le moteur bidi, branché sur « postProcessText », réordonne. Mais il ne
 *    DÉTECTE pas l'hébreu comme écriture de droite à gauche : sans
 *    « isInputRtl », « רחוב 45 » ressort inchangé — et ses chiffres inversés
 *    en « 54 ». L'arabe, lui, est détecté. D'où ces options, qui sont le seul
 *    jeu vérifié correct pour LES DEUX écritures.
 *
 * « setR2L » existe aussi, et il est à ÉCARTER : c'est un
 * « split('').reverse() » brut (jspdf.node.js, ligne 4737). Il retournerait la
 * ligne entière, chiffres compris — « 45 » deviendrait « 54 ». Le moteur bidi
 * fait exactement ce qu'il faut de plus : il réordonne les suites de droite à
 * gauche en laissant les nombres dans leur sens de lecture.
 */
export const OPTIONS_RTL = {
    isInputVisual: false,
    isOutputVisual: true,
    isInputRtl: true,
    isOutputRtl: false,
} as const;
